
import React from 'react';
import { Movie } from '../types';
import { motion } from 'framer-motion';
import SafetyBadge from './SafetyBadge';
import { Tv, Info, ThumbsUp, ThumbsDown } from 'lucide-react';

// 1. Import the default image
import movieNotFound from '../assets/movie_notfound.png';

interface MovieCardProps {
  movie: Movie;
  index: number;
  onClick: (movie: Movie) => void;
  onFeedback: (movie: Movie, liked: boolean) => void;
  isHistory?: boolean;
}

const MovieCard: React.FC<MovieCardProps> = ({ movie, index, onClick, onFeedback, isHistory }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.15, duration: 0.6 }}
      className="flex-shrink-0 w-[280px] md:w-[320px] group"
    >
      <div 
        className="relative aspect-[2/3] rounded-sm overflow-hidden border-2 border-[#d4af37]/50 shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-all duration-500 group-hover:border-[#d4af37] group-hover:scale-[1.02] bg-[#1a0101] cursor-pointer"
        onClick={() => onClick(movie)}
      >
        {/* Poster */}
        <img 
          src={movie.posterUrl} 
          alt={movie.title} 
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = movieNotFound;
          }}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
        
        {/* Film Strip Holes (Visual Decor) */}
        <div className="absolute left-2 inset-y-0 flex flex-col justify-around py-4">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="w-2 h-3 bg-black/40 rounded-sm"></div>
          ))}
        </div>
        <div className="absolute right-2 inset-y-0 flex flex-col justify-around py-4">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="w-2 h-3 bg-black/40 rounded-sm"></div>
          ))}
        </div>

        {/* Info Icon Hover */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
           <div className="bg-[#d4af37] p-2 rounded-full shadow-lg">
              <Info size={16} className="text-black" />
           </div>
        </div>

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
          <div className="mb-2">
            <span className="text-[10px] bg-[#d4af37] text-black px-1 font-bold rounded-sm uppercase tracking-tighter mr-2">
              {movie.rating}
            </span>
            <span className="text-[10px] text-[#d4af37] border border-[#d4af37] px-1 rounded-sm uppercase tracking-tighter">
              {movie.year}
            </span>
          </div>
          <h3 className="font-marquee text-lg font-bold text-white mb-2 leading-tight drop-shadow-lg">{movie.title}</h3>
          
          <div className="flex gap-2 mb-4">
            {movie.platforms.slice(0, 3).map((p, i) => (
              <div key={i} className="flex items-center gap-1 bg-white/10 px-2 py-0.5 rounded text-[10px] text-white/80">
                <Tv size={10} /> {p}
              </div>
            ))}
          </div>

          <SafetyBadge matrix={movie.safetyMatrix} />
        </div>
      </div>

      <div className="mt-4 px-2 space-y-4">
        <p className="text-xs text-[#fef3c7]/70 italic line-clamp-2 group-hover:line-clamp-none transition-all duration-300 font-serif-elegant">
          "{movie.criticNote}"
        </p>

        {!isHistory && (
          <div className="flex gap-3">
            <button 
              onClick={(e) => { e.stopPropagation(); onFeedback(movie, true); }}
              className="flex-1 flex items-center justify-center gap-2 border-2 border-[#d4af37]/30 text-[#d4af37] text-[10px] font-bold uppercase py-1.5 rounded-sm hover:bg-[#d4af37] hover:text-black transition-all"
            >
              <ThumbsUp size={12} /> Loved It
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onFeedback(movie, false); }}
              className="flex-1 flex items-center justify-center gap-2 border-2 border-[#d4af37]/30 text-[#d4af37] text-[10px] font-bold uppercase py-1.5 rounded-sm hover:bg-[#4a0404] hover:border-red-500 hover:text-red-500 transition-all"
            >
              <ThumbsDown size={12} /> Not For Us
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MovieCard;
